import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  AnimalAvatar,
  AvatarItem,
  ChildAvatarConfig,
  AvatarItemsByType,
} from '@/models/Avatar'
import {
  getAllAnimals,
  getAllAvatarItems,
  getAnimalsForLevel,
  getItemsForLevel,
  getUnlockedContent,
  groupItemsByType,
  getAnimal,
  getAvatarItem,
  getChildAvatarConfig,
  getChildAvatarConfigs,
  initializeChildAvatar,
  updateChildAnimal,
  equipAvatarItem,
  unequipAccessory,
  getChildAvatarDisplay,
} from '@/services/animal-avatar.service'
import { supabase } from '@/lib/supabase'
import type { ShopCollection } from '@/models/ShopItem'

export const useAvatarsStore = defineStore('avatars', () => {
  // State
  const childConfigs = ref<Record<string, ChildAvatarConfig>>({}); // Store by child ID
  const pendingAvatarLoads = new Map<string, Promise<void>>()
  const allAnimals = ref<AnimalAvatar[]>([])
  const allItems = ref<AvatarItem[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  // Cache for animals/items from shop (so shop-only animals/poses can be resolved for display)
  const shopAnimalsCache = ref<Record<string, AnimalAvatar>>({})
  const shopItemsCache = ref<Record<string, AvatarItem>>({})

  /**
   * Les collections (« Halloween 26 »), par identifiant.
   *
   * Lues par une requête À PART, et non embarquées dans celle du catalogue :
   * une relation que PostgREST ne connaît pas fait échouer toute la requête qui
   * la demande. Si l'app arrive avant la migration qui crée la table, le
   * catalogue d'avatars entier tomberait pour une simple étiquette. Ici, une
   * table absente donne seulement des objets sans collection.
   */
  const collectionsById = ref<Record<string, ShopCollection>>({})
  let collectionsInFlight: Promise<void> | null = null

  function loadCollections(): Promise<void> {
    // Les trois chargements de l'album partent en même temps : une seule
    // requête pour eux trois.
    if (collectionsInFlight) return collectionsInFlight
    collectionsInFlight = (async () => {
      const { data, error } = await supabase
        .from('shop_collections' as any)
        .select('id, slug, name, name_translations')
      if (error) return
      collectionsById.value = Object.fromEntries(
        ((data ?? []) as any[]).map(c => [c.id, { slug: c.slug, name: c.name, name_translations: c.name_translations ?? null }]),
      )
    })().finally(() => { collectionsInFlight = null })
    return collectionsInFlight
  }

  const collectionOf = (row: any): ShopCollection | null =>
    (row?.collection_id && collectionsById.value[row.collection_id]) || null

  // Helper function to get config for a specific child
  const getConfigForChild = (childId: string | undefined) => {
    if (!childId) return null;
    return childConfigs.value[childId] || null;
  }

  // Helper functions to get avatar data for a specific child
  // Resolve from config first, then from shop cache (for shop-only animals/poses)
  const getAnimalForChild = (childId: string | undefined) => {
    const config = getConfigForChild(childId);
    if (!config) return null;
    return getAnimal(config.animalId) || shopAnimalsCache.value[config.animalId] || null;
  }

  const getPoseForChild = (childId: string | undefined) => {
    const config = getConfigForChild(childId);
    if (!config?.equippedPose) return null;
    return getAvatarItem(config.equippedPose) || shopItemsCache.value[config.equippedPose] || null;
  }

  const getAccessoriesForChild = (childId: string | undefined) => {
    const config = getConfigForChild(childId);
    if (!config) return [];
    return config.equippedAccessories
      .map((id) => getAvatarItem(id))
      .filter((item): item is AvatarItem => item !== undefined);
  }

  const getBackgroundForChild = (childId: string | undefined) => {
    const config = getConfigForChild(childId);
    if (!config?.equippedBackground) return null;
    return getAvatarItem(config.equippedBackground) || shopItemsCache.value[config.equippedBackground] || null;
  }

  // Legacy computed properties (for backward compatibility with single-child views)
  const currentChildConfig = computed(() => {
    // Return the first config if any exists (for backward compatibility)
    const configs = Object.values(childConfigs.value);
    return configs[0] || null;
  })

  const currentAnimal = computed(() => {
    if (!currentChildConfig.value) return null
    return getAnimal(currentChildConfig.value.animalId) || shopAnimalsCache.value[currentChildConfig.value.animalId] || null
  })

  const currentPose = computed(() => {
    if (!currentChildConfig.value?.equippedPose) return null
    return getAvatarItem(currentChildConfig.value.equippedPose) || shopItemsCache.value[currentChildConfig.value.equippedPose] || null
  })

  const currentAccessories = computed(() => {
    if (!currentChildConfig.value) return []
    return currentChildConfig.value.equippedAccessories
      .map((id) => getAvatarItem(id))
      .filter((item): item is AvatarItem => item !== undefined)
  })

  const currentBackground = computed(() => {
    if (!currentChildConfig.value?.equippedBackground) return null
    return getAvatarItem(currentChildConfig.value.equippedBackground) || shopItemsCache.value[currentChildConfig.value.equippedBackground] || null
  })

  // Actions

  /**
   * Initialize store with all available animals and items
   */
  function loadAllContent() {
    allAnimals.value = getAllAnimals()
    allItems.value = getAllAvatarItems()
  }

  /**
   * Load available animals for a child from shop_items (including purchased ones)
   * This replaces the static config with dynamic shop_items data
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function loadAvailableAnimalsForChild(childId: string, childLevel: number): Promise<AnimalAvatar[]> {
    try {
      // Get all avatar shop_items that are active
      const [{ data: avatarShopItems, error: shopError }] = await Promise.all([
        supabase
          .from('shop_items' as any)
          .select('*')
          .eq('type', 'animal')
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        loadCollections(),
      ])

      if (shopError) {
        console.error('Error loading shop items:', shopError)
        // Fallback to config animals
        return getAllAnimals()
      }

      // Get purchased avatar items for this child. Le type et le lien vers
      // l'animal viennent avec : une pose possédée débloque son animal.
      const { data: purchases, error: purchaseError } = await supabase
        .from('child_shop_purchases' as any)
        .select('shop_item_id, shop_item:shop_items(type, metadata)')
        .eq('child_id', childId)

      if (purchaseError) {
        console.error('Error loading purchases:', purchaseError)
      }

      // Reuse in-memory avatar config when already loaded to avoid duplicate child_avatars requests.
      let equippedAnimalId = childConfigs.value[childId]?.animalId || null
      if (!equippedAnimalId) {
        // Get currently equipped avatar from child_avatars
        // This ensures default avatars assigned during onboarding are considered available
        const { data: avatarConfig } = await supabase
          .from('child_avatars' as any)
          .select('animal_id')
          .eq('child_id', childId)
          .single()

        // Get child's current avatar from children table as fallback
        const { data: childData } = await supabase
          .from('children' as any)
          .select('avatar')
          .eq('id', childId)
          .single()

        equippedAnimalId = (avatarConfig as any)?.animal_id || (childData as any)?.avatar || null
      }

      // Create a set of purchased shop_item IDs
      const purchasedShopItemIds = new Set<string>()
      // Un animal est une catégorie (2026-09-17) : il est débloqué dès qu'une de
      // ses poses l'est. La base inscrit aussi l'animal avec la pose ; lire les
      // poses ici tient même pour un achat antérieur à ce déclencheur.
      const animalsFromPoses = new Set<string>()
      if (purchases) {
        purchases.forEach((purchase: any) => {
          if (purchase.shop_item_id) {
            purchasedShopItemIds.add(purchase.shop_item_id)
          }
          const item = purchase.shop_item
          if (item?.type === 'pose' && item.metadata?.animal_id) {
            animalsFromPoses.add(item.metadata.animal_id)
          }
        })
      }

      // Map shop_items to AnimalAvatar format
      // Shop-only unlock logic: items are available if purchased OR if currently equipped (default avatar from onboarding)
      const availableAnimals: AnimalAvatar[] = (avatarShopItems || []).map((shopItem: any) => {
        const metadata = shopItem.metadata || {}
        const animalId = metadata.animal_id || shopItem.id
        const unlockLevel = metadata.unlock_level || 0
        const isPurchased = purchasedShopItemIds.has(shopItem.id)

        // Shop-only: available if purchased OR if currently equipped (default avatar from onboarding)
        // This ensures that avatars assigned during onboarding are always considered available
        const isCurrentlyEquipped = equippedAnimalId && animalId === equippedAnimalId
        const isAvailable = isPurchased || !!isCurrentlyEquipped || animalsFromPoses.has(animalId)

        // Un animal ne s'achète plus : il se montre librement, ce sont ses poses
        // qui se paient. Seul un animal réservé (exclusif) reste caché à qui ne
        // l'a pas.
        if (!isAvailable && shopItem.is_exclusive === true) return null

        // Try to get the animal from config for base image and other properties
        const configAnimal = getAnimal(animalId)

        return {
          id: animalId,
          name: shopItem.name,
          description: shopItem.description || configAnimal?.description || '',
          name_translations: shopItem.name_translations ?? null,
          description_translations: shopItem.description_translations ?? null,
          baseImage: shopItem.image_url || configAnimal?.baseImage || '',
          unlockLevel: unlockLevel,
          // Store additional shop_item info for reference
          shop_item_id: shopItem.id,
          price: shopItem.price, // Include price for purchase functionality
          rarity: shopItem.rarity, // Include rarity for display
          // Fenêtre de parution : l'album s'en sert pour sa série « Nouveautés ».
          released_at: (shopItem as any).released_at ?? null,
          available_until: (shopItem as any).available_until ?? null,
          collection: collectionOf(shopItem),
          is_purchased: isPurchased,
          // Shop-only: available if purchased OR currently equipped
          is_available: isAvailable,
        } as AnimalAvatar & { shop_item_id?: string; price?: number; rarity?: 'basic' | 'standard' | 'premium'; is_purchased?: boolean; is_available?: boolean }
      })
        .filter((a: AnimalAvatar | null): a is AnimalAvatar => a !== null)

      // Cache for display resolution (getAnimalForChild) when animal is shop-only
      availableAnimals.forEach((a) => {
        shopAnimalsCache.value[a.id] = a
      })

      return availableAnimals
    } catch (err: any) {
      console.error('Error loading available animals for child:', err)
      // Fallback to config animals
      return getAllAnimals()
    }
  }

  /**
   * Load available avatar items (poses, backgrounds) for a child from shop_items
   * Includes purchased items and level-unlocked items
   * @param childId The child's ID
   * @param childLevel The child's current level
   * @param itemType Filter by type ('pose' or 'background'), or undefined for all
   * @param animalId Optional filter for animal-specific poses
   */
  async function loadAvailableItemsForChild(
    childId: string,
    childLevel: number,
    itemType?: 'pose' | 'background',
    animalId?: string
  ): Promise<AvatarItem[]> {
    try {
      // Build query for shop_items
      let query = supabase
        .from('shop_items' as any)
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })

      // Filter by type if specified
      if (itemType) {
        query = query.eq('type', itemType)
      } else {
        // Get both poses and backgrounds
        query = query.in('type', ['pose', 'background'])
      }

      const [{ data: shopItems, error: shopError }] = await Promise.all([query, loadCollections()])

      if (shopError) {
        console.error('Error loading shop items:', shopError)
        // Fallback to config items
        return itemType ? getAllAvatarItems().filter(item => item.type === itemType) : getAllAvatarItems()
      }

      // Get purchased items for this child
      const { data: purchases, error: purchaseError } = await supabase
        .from('child_shop_purchases' as any)
        .select('shop_item_id')
        .eq('child_id', childId)

      if (purchaseError) {
        console.error('Error loading purchases:', purchaseError)
      }

      // Create set of purchased shop_item IDs
      const purchasedShopItemIds = new Set<string>()
      if (purchases) {
        purchases.forEach((purchase: any) => {
          if (purchase.shop_item_id) {
            purchasedShopItemIds.add(purchase.shop_item_id)
          }
        })
      }

      // Les items équipés (pose/fond par défaut de l'onboarding, jamais
      // « achetés ») doivent rester visibles même s'ils ne sont pas obtenables.
      const cfg = childConfigs.value[childId]
      const equippedItemIds = new Set(
        [cfg?.equippedPose, cfg?.equippedBackground].filter(Boolean) as string[]
      )

      // Map shop_items to AvatarItem format
      // Shop-only unlock logic: items are available only if purchased (or from Chest rewards)
      const availableItems: AvatarItem[] = (shopItems || [])
        .map((shopItem: any) => {
          const metadata = shopItem.metadata || {}
          const unlockLevel = metadata.unlock_level || 0
          const isPurchased = purchasedShopItemIds.has(shopItem.id)

          // Extract item type and ID from metadata
          const itemAnimalId = metadata.animal_id || 'all'
          const itemId = metadata.pose_id || metadata.background_id || shopItem.id

          // Filter by animal if specified (for poses)
          if (animalId && shopItem.type === 'pose' && itemAnimalId !== animalId && itemAnimalId !== 'all') {
            return null
          }

          // Ne montrer un item NON possédé (et non équipé) que s'il est
          // réellement obtenable : achetable, non exclusif (coffres) et avec un
          // prix. Aligne la visibilité sur shopService.getShopItems.
          const isObtainable = shopItem.is_purchasable !== false && shopItem.is_exclusive !== true && (shopItem.price ?? 0) > 0
          if (!isPurchased && !equippedItemIds.has(itemId) && !isObtainable) {
            return null
          }

          return {
            id: itemId,
            type: shopItem.type as 'pose' | 'background',
            animalId: itemAnimalId,
            name: shopItem.name,
            name_translations: shopItem.name_translations ?? null,
            description_translations: shopItem.description_translations ?? null,
            imagePath: shopItem.image_url || '',
            unlockLevel: unlockLevel,
            category: shopItem.category || 'default',
            // Shop integration properties
            shop_item_id: shopItem.id,
            is_purchased: isPurchased,
            // Shop-only: available only if purchased (no level-based unlock)
            is_available: isPurchased,
            price: shopItem.price,
            rarity: shopItem.rarity,
            released_at: (shopItem as any).released_at ?? null,
            available_until: (shopItem as any).available_until ?? null,
            collection: collectionOf(shopItem),
          } as AvatarItem
        })
        .filter((item): item is AvatarItem => item !== null)

      // Cache for display resolution (getPoseForChild / getBackgroundForChild) when item is shop-only
      availableItems.forEach((item) => {
        shopItemsCache.value[item.id] = item
      })

      return availableItems
    } catch (err: any) {
      console.error('Error loading available items for child:', err)
      // Fallback to config items
      return itemType ? getAllAvatarItems().filter(item => item.type === itemType) : getAllAvatarItems()
    }
  }

  /**
   * Hydrate shop caches so that the current config's animal/pose/background
   * can be resolved for display (e.g. in ChildHeader) even before the user
   * opens the avatar modal. Fixes missing avatar image on reload for shop-only animals.
   */
  async function hydrateShopCacheForConfig(config: ChildAvatarConfig) {
    const needAnimal = config.animalId && !getAnimal(config.animalId) && !shopAnimalsCache.value[config.animalId]
    if (needAnimal) {
      const { data } = await supabase
        .from('shop_items' as any)
        .select('id, name, description, name_translations, description_translations, image_url, metadata')
        .eq('type', 'animal')
        .eq('is_active', true)
      const rows = (data || []) as any[]
      const shopItem = rows.find((r: any) => (r.metadata?.animal_id || r.id) === config.animalId)
      if (shopItem) {
        const metadata = shopItem.metadata || {}
        const animalId = metadata.animal_id || shopItem.id
        shopAnimalsCache.value[animalId] = {
          id: animalId,
          name: shopItem.name,
          description: shopItem.description || '',
          name_translations: shopItem.name_translations ?? null,
          description_translations: shopItem.description_translations ?? null,
          baseImage: shopItem.image_url || '',
          unlockLevel: metadata.unlock_level ?? 0,
        } as AnimalAvatar
      }
    }

    const needPose = config.equippedPose && !getAvatarItem(config.equippedPose) && !shopItemsCache.value[config.equippedPose]
    if (needPose) {
      const { data } = await supabase
        .from('shop_items' as any)
        .select('id, name, name_translations, description_translations, image_url, metadata, type, category')
        .eq('type', 'pose')
        .eq('is_active', true)
      const poseRows = (data || []) as any[]
      const shopItem = poseRows.find((r: any) => r.metadata?.pose_id === config.equippedPose)
      if (shopItem) {
        const metadata = shopItem.metadata || {}
        const itemId = metadata.pose_id || shopItem.id
        shopItemsCache.value[itemId] = {
          id: itemId,
          type: 'pose',
          animalId: metadata.animal_id || 'all',
          name: shopItem.name,
          name_translations: shopItem.name_translations ?? null,
          description_translations: shopItem.description_translations ?? null,
          imagePath: shopItem.image_url || '',
          unlockLevel: metadata.unlock_level ?? 0,
          category: shopItem.category || 'default',
        } as AvatarItem
      }
    }

    const needBackground = config.equippedBackground && !getAvatarItem(config.equippedBackground) && !shopItemsCache.value[config.equippedBackground]
    if (needBackground) {
      const { data } = await supabase
        .from('shop_items' as any)
        .select('id, name, name_translations, description_translations, image_url, metadata, type, category')
        .eq('type', 'background')
        .eq('is_active', true)
      const bgRows = (data || []) as any[]
      const shopItem = bgRows.find((r: any) => r.metadata?.background_id === config.equippedBackground)
      if (shopItem) {
        const metadata = shopItem.metadata || {}
        const itemId = metadata.background_id || shopItem.id
        shopItemsCache.value[itemId] = {
          id: itemId,
          type: 'background',
          animalId: 'all',
          name: shopItem.name,
          name_translations: shopItem.name_translations ?? null,
          description_translations: shopItem.description_translations ?? null,
          imagePath: shopItem.image_url || '',
          unlockLevel: metadata.unlock_level ?? 0,
          category: shopItem.category || 'default',
        } as AvatarItem
      }
    }
  }

  /**
   * Preload avatar configs for many children in a single query, then hydrate
   * the shop caches so each one resolves for display. Call this once when a
   * multi-kid view mounts (Tasks/Rewards tab Swipers) so the per-slide
   * loadChildAvatar() calls hit the cache instead of each firing its own
   * child_avatars request (HARMONIA-7).
   */
  async function preloadChildAvatars(childIds: string[]) {
    const missing = childIds.filter(
      (id) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) &&
        !childConfigs.value[id]
    )
    if (missing.length === 0) return

    try {
      const configs = await getChildAvatarConfigs(missing)
      for (const [childId, config] of configs) {
        childConfigs.value[childId] = config
        // Resolve shop-only animal/pose/background on first paint, like loadChildAvatar.
        await hydrateShopCacheForConfig(config)
      }
      // Children with no row yet are left for loadChildAvatar to initialize lazily.
    } catch (err) {
      console.error('Error preloading child avatars:', err)
    }
  }

  /**
   * Load child's avatar configuration from database
   */
  async function loadChildAvatar(childId: string, forceReload = false) {
    // Skip temporary onboarding IDs (not yet persisted as real UUIDs)
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(childId)) {
      return
    }
    // Don't reload if already cached (unless forced)
    if (childConfigs.value[childId] && !forceReload) {
      return;
    }

    // Deduplicate concurrent requests for the same child.
    if (!forceReload) {
      const pendingLoad = pendingAvatarLoads.get(childId)
      if (pendingLoad) {
        await pendingLoad
        return
      }
    }

    const loadPromise = (async () => {
      loading.value = true
      error.value = null

      try {
        const config = await getChildAvatarConfig(childId)

        if (!config) {
          // No config found, initialize with default (dog - first animal at level 0)
          const result = await initializeChildAvatar(childId, 'dog')
          if (result.success) {
            const newConfig = await getChildAvatarConfig(childId)
            if (newConfig) {
              childConfigs.value[childId] = newConfig
            }
          } else {
            throw new Error(result.error || 'Failed to initialize avatar')
          }
        } else {
          childConfigs.value[childId] = config
          // So that shop-only animal/pose/background resolve on first paint (e.g. ChildHeader after reload)
          await hydrateShopCacheForConfig(config)
        }
      } catch (err: any) {
        console.error('Error loading child avatar:', err)
        error.value = err.message || 'Failed to load avatar'
      } finally {
        loading.value = false
      }
    })()

    pendingAvatarLoads.set(childId, loadPromise)
    try {
      await loadPromise
    } finally {
      if (pendingAvatarLoads.get(childId) === loadPromise) {
        pendingAvatarLoads.delete(childId)
      }
    }
  }

  /**
   * Get unlocked animals for a specific level
   */
  function getUnlockedAnimals(level: number): AnimalAvatar[] {
    return getAnimalsForLevel(level)
  }

  /**
   * Get unlocked items for a specific level
   */
  function getUnlockedItems(level: number, animalId?: string, itemType?: string): AvatarItem[] {
    return getItemsForLevel(level, animalId, itemType)
  }

  /**
   * Get all unlocked content for a level
   */
  function getUnlockedContentForLevel(level: number, animalId?: string) {
    return getUnlockedContent(level, animalId)
  }

  /**
   * Group items by type (poses, accessories, backgrounds)
   */
  function getItemsByType(items: AvatarItem[]): AvatarItemsByType {
    return groupItemsByType(items)
  }

  /**
   * Check if an animal is unlocked
   * Shop-only: checks purchases only, not level
   * @param animalId Animal identifier
   * @param level Child's level (kept for compatibility, not used)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function isAnimalUnlocked(animalId: string, level: number): boolean {
    // Shop-only unlock: check if animal is in purchased items
    // Note: This is a simplified check - full check should query child_shop_purchases
    // For now, we rely on loadAvailableAnimalsForChild which sets is_available based on purchases
    // This function signature is kept for backward compatibility but should be refactored
    // to accept childId and check purchases directly
    return false; // Conservative: return false, let loadAvailableAnimalsForChild handle it
  }

  /**
   * Check if an item is unlocked
   * Shop-only: checks purchases only, not level
   * @param itemId Item identifier
   * @param level Child's level (kept for compatibility, not used)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function isItemUnlocked(itemId: string, level: number): boolean {
    // Shop-only unlock: check if item is in purchased items
    // Note: This is a simplified check - full check should query child_shop_purchases
    // For now, we rely on loadAvailableItemsForChild which sets is_available based on purchases
    // This function signature is kept for backward compatibility but should be refactored
    // to accept childId and check purchases directly
    return false; // Conservative: return false, let loadAvailableItemsForChild handle it
  }

  /**
   * Change child's animal
   */
  async function changeAnimal(childId: string, animalId: string, childLevel: number) {
    loading.value = true
    error.value = null

    // Store previous config for rollback
    const previousConfig = childConfigs.value[childId]

    try {
      // Shop-only unlock: check if animal is purchased
      // Load available animals to check purchase status
      const availableAnimals = await loadAvailableAnimalsForChild(childId, childLevel);
      const animal = availableAnimals.find(a => a.id === animalId);
      if (!animal || !animal.is_available) {
        throw new Error('Animal not unlocked yet')
      }

      // Optimistic update - update cache immediately
      if (childConfigs.value[childId]) {
        childConfigs.value[childId] = {
          ...childConfigs.value[childId],
          animalId,
        }
      }

      const result = await updateChildAnimal(childId, animalId)

      if (!result.success) {
        throw new Error(result.error || 'Failed to change animal')
      }

      // Reload to ensure sync with database
      await loadChildAvatar(childId, true)
    } catch (err: any) {
      console.error('Error changing animal:', err)
      error.value = err.message || 'Failed to change animal'

      // Rollback on error
      if (previousConfig) {
        childConfigs.value[childId] = previousConfig
      }

      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Equip an avatar item
   */
  async function equipItem(childId: string, itemId: string, childLevel: number) {
    loading.value = true
    error.value = null

    // Store previous config for rollback
    const previousConfig = childConfigs.value[childId]

    try {
      // Shop-only unlock: check if item is purchased
      // Load available items to check purchase status
      const availableItems = await loadAvailableItemsForChild(childId, childLevel);
      const purchasedItem = availableItems.find(i => i.id === itemId);
      if (!purchasedItem || !purchasedItem.is_available) {
        throw new Error('Item not unlocked yet')
      }

      // Get item details from config, fallback to shop data (shop-only items aren't in static config)
      const itemDetails = getAvatarItem(itemId) || purchasedItem
      if (!itemDetails) {
        throw new Error('Item not found')
      }

      // Optimistic update - update cache immediately
      if (childConfigs.value[childId]) {
        const config = { ...childConfigs.value[childId] }

        if (itemDetails.type === 'pose') {
          config.equippedPose = itemId
        } else if (itemDetails.type === 'accessory') {
          if (!config.equippedAccessories.includes(itemId)) {
            config.equippedAccessories = [...config.equippedAccessories, itemId]
          }
        } else if (itemDetails.type === 'background') {
          config.equippedBackground = itemId
        }

        childConfigs.value[childId] = config
      }

      const result = await equipAvatarItem(childId, itemId, { childLevel, itemOverride: itemDetails })

      if (!result.success) {
        throw new Error(result.error || 'Failed to equip item')
      }

      // Reload to ensure sync with database
      await loadChildAvatar(childId, true)
    } catch (err: any) {
      const message = err?.message ?? (typeof err === 'object' && err !== null && 'message' in err ? String((err as { message?: unknown }).message) : null) ?? 'Failed to equip item'
      console.error('Error equipping item:', message, err)
      error.value = message

      // Rollback on error
      if (previousConfig) {
        childConfigs.value[childId] = previousConfig
      }

      throw new Error(message)
    } finally {
      loading.value = false
    }
  }

  /**
   * Unequip an accessory
   */
  async function removeAccessory(childId: string, itemId: string) {
    loading.value = true
    error.value = null

    // Store previous config for rollback
    const previousConfig = childConfigs.value[childId]

    try {
      // Optimistic update - update cache immediately
      if (childConfigs.value[childId]) {
        const config = { ...childConfigs.value[childId] }
        config.equippedAccessories = config.equippedAccessories.filter(id => id !== itemId)
        childConfigs.value[childId] = config
      }

      const result = await unequipAccessory(childId, itemId)

      if (!result.success) {
        throw new Error(result.error || 'Failed to remove accessory')
      }

      // Reload to ensure sync with database
      await loadChildAvatar(childId, true)
    } catch (err: any) {
      console.error('Error removing accessory:', err)
      error.value = err.message || 'Failed to remove accessory'

      // Rollback on error
      if (previousConfig) {
        childConfigs.value[childId] = previousConfig
      }

      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get full avatar display data
   */
  async function getAvatarDisplay(childId: string) {
    return getChildAvatarDisplay(childId)
  }

  /**
   * Reset current child config (when navigating away)
   */
  function resetCurrentConfig() {
    error.value = null
  }

  /**
   * Clear a specific child's config (force reload)
   */
  function clearChildConfig(childId: string) {
    delete childConfigs.value[childId]
  }

  /**
   * Clear all cached configs
   */
  function clearAllConfigs() {
    childConfigs.value = {}
    error.value = null
  }

  return {
    // State
    currentChildConfig,
    allAnimals,
    allItems,
    loading,
    error,

    // Computed (legacy)
    currentAnimal,
    currentPose,
    currentAccessories,
    currentBackground,

    // Per-child getters
    getConfigForChild,
    getAnimalForChild,
    getPoseForChild,
    getAccessoriesForChild,
    getBackgroundForChild,

    // Actions
    loadAllContent,
    loadChildAvatar,
    preloadChildAvatars,
    loadAvailableAnimalsForChild,
    loadAvailableItemsForChild,
    getUnlockedAnimals,
    getUnlockedItems,
    getUnlockedContentForLevel,
    getItemsByType,
    isAnimalUnlocked,
    isItemUnlocked,
    changeAnimal,
    equipItem,
    removeAccessory,
    getAvatarDisplay,
    resetCurrentConfig,
    clearChildConfig,
    clearAllConfigs,
  }
})
